import {AbstractEntity} from "@/domain/entity/AbstractEntity.ts";
import type {RouteLocationAsRelative} from "vue-router";
import type {Source} from "@/domain/type/source.type.ts";
import {type CardData, EntityAction} from "@/domain/type/entity.type.ts";
import {RouteEnum} from "@/router/RouteEnum.ts";
import {useEntitiesStore} from "@/stores/entities.store.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";
import type {LocationDetails} from "@/domain/type/location.type.ts";
import {RequestEntityState, RequestEntityStatus, type SourceDetails} from "@/domain/type/synchro.api.type.ts";

export class Location extends AbstractEntity {
  cardIcon = 'mdi-map-marker'
  left: LocationDetails = {
    name: '',
    code: '',
    structure: '',
    idEva: '',
    link: ''
  }
  right: LocationDetails = {
    name: '',
    code: '',
    structure: '',
    idEva: '',
    link: ''
  }

  constructor(
    uuid: string,
    leftFromApi: SourceDetails[],
    rightFromApi: SourceDetails[][],
    state: RequestEntityState,
    status: RequestEntityStatus
  ) {
    super(uuid, leftFromApi, rightFromApi, state, status);
    this.left = this.hydrateDetails(leftFromApi);
    this.right = this.hydrateDetails(rightFromApi[0] ?? []);
    this.leftLink = this.left.link;
    if (this.right.link === '') {
      if (import.meta.env.VITE_AMMON_BASE_URL_OLD === import.meta.env.VITE_AMMON_BASE_URL) {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL_OLD + '/AMMON/COMMUN/Lieux/LieuxGrille.aspx?AEF=1';
      } else {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL + '/BO/s/COMMUN/Lieux/LieuxGrille.aspx?AEF=1';
      }
    } else {
      this.rightLink = this.right.link;
    }
  }


  fusionLink(): RouteLocationAsRelative {
    return {
      name: RouteEnum.Fusion,
      params: {
        id: this.left.idEva,
        sourceLeft: useEntitiesStore().sourceLeft,
        sourceRight: useEntitiesStore().sourceRight,
        type: SynchroType.LOCATION
      }
    }
  }

  getActions(): EntityAction[] {
    return [
      EntityAction.success,
      EntityAction.fusion,
      EntityAction.create,
    ];
  }

  getCreateActionText(srcRight: Source): string {
    const target = srcRight.charAt(0).toUpperCase() + srcRight.slice(1).toLowerCase();
    return 'Êtes-vous certain de vouloir créer ce lieux dans ' + target + '?';
  }

  public getRightCardData(): CardData[] {
    return this.getCardData(this.right);
  }

  public getLeftCardData(): CardData[] {
    return this.getCardData(this.left);
  }

  match(filter: string): boolean {
    return this.left.name.toLowerCase().includes(filter)
      || this.left.code.toLowerCase().includes(filter)
      || this.left.structure.toLowerCase().includes(filter)
      || this.left.idEva.toString().toLowerCase().includes(filter);
  }

  protected hydrateDetails(sourceDetails: SourceDetails[]): any {
    const details: LocationDetails = {
      name: '',
      code: '',
      structure: '',
      idEva: '',
      link: ''
    }

    sourceDetails.forEach((detail) => {
      switch (detail.label) {
        case 'Nom':
          details.name = String(detail.val)
          break
        case 'Code':
          details.code = String(detail.val)
          break
        case 'idEva':
          details.idEva = String(detail.val)
          break
        case 'Structure':
          details.structure = String(detail.val)
          break
        case 'Lien':
          details.link = this.filterLink(String(detail.val));
      }
    });
    return details;
  }

  private getCardData(details: LocationDetails): CardData[] {
    return [
      {
        label: 'Nom',
        value: details.name
      },
      {
        label: 'Code',
        value: details.code
      },
      {
        label: 'Structure',
        value: details.structure
      }
    ];
  }
}
